import React, { useState, useEffect } from 'react';
import { useTranslation } from '../utils/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import ModuleCard from '../components/modules/ModuleCard';
import ComplexityFilter from '../components/modules/ComplexityFilter';
import SearchHelp from '../components/modules/SearchHelp';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ModulesPage = () => {
  const { t, language } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedComplexity, setSelectedComplexity] = useState(searchParams.get('complexity') || null);

  // Initialize search query from URL parameters
  useEffect(() => {
    const searchFromURL = searchParams.get('search');
    if (searchFromURL) {
      const decodedSearch = decodeURIComponent(searchFromURL)
        .replace(/\+/g, ' ')
        .trim();
      setSearchQuery(decodedSearch);
    }
  }, []);

  // Parse search query into search patterns
  const parseSearchQuery = (query) => {
    if (!query) return [];
    
    const orGroups = query.split('|').map(group => group.trim());
    
    return orGroups.map(group => {
      const andTerms = group.split(/\s+AND\s+/).map(term => term.trim());
      
      const patterns = andTerms.map(term => {
        if (term.startsWith('"') && term.endsWith('"')) {
          return {
            type: 'exact_phrase',
            value: term.slice(1, -1)
          };
        }
        
        if (term.startsWith('**') && term.endsWith('**')) {
          return {
            type: 'exact_match',
            value: term.slice(2, -2)
          };
        }
        
        return {
          type: 'partial',
          value: term
        };
      });
      
      return {
        type: 'or_group',
        patterns: patterns.filter(p => p.value.length > 0)
      };
    }).filter(group => group.patterns.length > 0);
  };

  const matchesSearchPattern = (text, pattern) => {
    if (!text) return false;
    
    const normalizedText = text.toLowerCase();
    const normalizedPattern = pattern.value.toLowerCase();
    
    switch (pattern.type) {
      case 'exact_phrase':
        return normalizedText.includes(normalizedPattern);
        
      case 'exact_match':
        return normalizedText === normalizedPattern ||
               normalizedText.split(/\s+/).includes(normalizedPattern);
        
      case 'partial':
        const abbreviatedText = normalizedText.replace(/[^a-z0-9]/g, '');
        const abbreviatedPattern = normalizedPattern.replace(/[^a-z0-9]/g, '');
        return normalizedText.includes(normalizedPattern) ||
               abbreviatedText.includes(abbreviatedPattern);
        
      default:
        return false;
    }
  };

  useEffect(() => {
    const loadModules = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let response;
        try {
          response = await fetch(`/data_copy/type=data_course_master/language=${language}/course_modules__${language}.json`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
        } catch (fetchError) {
          console.error('Fetch error:', fetchError);
          throw new Error(t('errorTitle'));
        }

        if (!response.ok && language !== 'en') {
          const enResponse = await fetch('/digital_horizon_course_overview_simple__en.json');
          if (!enResponse.ok) throw new Error(t('errorTitle'));
          const data = await enResponse.json();
          setModules(data);
        } else {
          const data = await response.json();
          setModules(data);
        }
      } catch (err) {
        console.error('Error loading modules:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadModules();
  }, [language, t]);

  // Load lessons
  useEffect(() => {
    const loadLessons = async () => {
      try {
        const response = await fetch(`/data_copy/type=data_course_master/language=${language}/course_lessons__${language}.json`, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok && language !== 'en') {
          const enResponse = await fetch('/data_copy/type=data_course_master/language=${language}/course_lessons__en.json');
          if (!enResponse.ok) throw new Error(t('errorTitle'));
          const data = await enResponse.json();
          setLessons(data);
        } else {
          const data = await response.json();
          setLessons(data);
        }
      } catch (err) {
        console.error('Error loading lessons:', err);
      }
    };

    loadLessons();
  }, [language, t]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedComplexity) params.set('complexity', selectedComplexity);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedComplexity, setSearchParams]);

  const searchPatternsGroups = parseSearchQuery(searchQuery);

  const filteredModules = modules.filter(module => {
    if (!searchQuery.trim() && !selectedComplexity) {
      return true;
    }
  
    const searchableText = [
      module.name,
      module.name_code,
      module.learning_objectives,
      module.top_skills,
      module.prerequisites
    ].filter(Boolean).join(' ');
  
    const matchesSearch = searchPatternsGroups.length === 0 || 
      searchPatternsGroups.some(group => 
        group.patterns.every(pattern => 
          matchesSearchPattern(searchableText, pattern)
        )
      );
  
    const matchesComplexity = !selectedComplexity || 
      module.complexity_level === selectedComplexity;
    
    return matchesSearch && matchesComplexity;
  });

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('modulesPage.title')}
          </h1>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative flex-1 max-w-lg">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('modulesPage.search.placeholder')}
                  className="pl-10 pr-24 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                  <SearchHelp />
                </div>
              </div>
            </div>
            <ComplexityFilter
              selectedLevel={selectedComplexity}
              onLevelChange={setSelectedComplexity}
            />
          </div>

          {filteredModules.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredModules.map((module) => (
                <ModuleCard 
                  key={module.name_code} 
                  module={module}
                  lessons={lessons}
                  searchPhrases={searchPatternsGroups.flatMap(group => 
                    group.patterns.map(p => p.value)
                  )}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('modulesPage.search.noResults')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModulesPage;